import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function ServicesPage() {
  const [scrolled, setScrolled] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fundingPlans = [
    {
      title: '青年創業專案',
      target: '設立未滿五年之新創企業',
      description: '專為 18–45 歲創業者設計，提供低利、免保人、前五年只付利息之優惠方案，協助青年圓夢。',
      highlights: [
        '最高額度 1,200 萬',
        '100 萬以下免保人',
        '利息補貼優惠',
        '最長年期 6 年'
      ],
      icon: 'ri-rocket-line',
      color: 'orange'
    },
    {
      title: '中小企業信保專案',
      target: '成立一年以上之中小企業',
      description: '透過信保基金提供信用保證，協助企業解決擔保品不足問題，順利取得銀行融資。',
      highlights: [
        '最高保證成數 9.5 成',
        '適用各類營運週轉',
        '活化企業信用',
        '利率由銀行核定'
      ],
      icon: 'ri-shield-check-line',
      color: 'blue'
    },
    {
      title: '微型企業／小頭家專案',
      target: '5 人以下微型企業、自營商',
      description: '針對規模較小、僅有稅籍登記之經營者，提供簡易快速融資方案。',
      highlights: [
        '手續簡便快速',
        '額度約 50–100 萬',
        '不限設立年限',
        '適用自營商／攤商'
      ],
      icon: 'ri-store-2-line',
      color: 'teal'
    },
    {
      title: '文化創意產業優惠專案',
      target: '從事文創產業之企業',
      description: '支持藝術、音樂、表演、設計等文創領域，提供專屬優惠融資條件。',
      highlights: [
        '利息補貼最高 2%',
        '額度彈性、無上限',
        '信保基金加成保證',
        '無形資產亦可評估'
      ],
      icon: 'ri-palette-line',
      color: 'purple'
    },
    {
      title: '低碳智慧化設備補助',
      target: '欲進行智慧／低碳轉型之工廠',
      description: '鼓勵製造業導入智慧化或低碳設備進行轉型，政府提供高額補助款（免償還）。',
      highlights: [
        '補助金額約 500–3000 萬',
        '補助款無須償還',
        '包含設備與軟體',
        '提升製程效率'
      ],
      icon: 'ri-leaf-line',
      color: 'green'
    },
    {
      title: '再生能源／節能設備專案',
      target: '購置綠能或節能設備之企業',
      description: '支援企業導入再生能源或節能設備，協助 ESG 永續發展。',
      highlights: [
        '最高融資成數 8 成',
        '利率優惠',
        '最長年期 15 年',
        '支持綠能轉型'
      ],
      icon: 'ri-seedling-line',
      color: 'emerald'
    }
  ];

  const services = [
    {
      icon: 'ri-funds-line',
      title: '企業融資媒合',
      description: '為企業尋找最適合的資金來源，包含銀行貸款、民間融資、股權投資等多元管道',
      features: [
        '快速評估企業資金需求',
        '媒合最適合的資金方案',
        '協助準備申請文件',
        '全程陪同洽談協商'
      ],
      color: 'amber'
    },
    {
      icon: 'ri-building-line',
      title: '不動產融資',
      description: '提供土地、廠房、商辦等不動產抵押融資服務，靈活運用資產價值',
      features: [
        '不動產價值評估',
        '最高可貸成數分析',
        '利率方案比較',
        '快速撥款服務'
      ],
      color: 'blue'
    },
    {
      icon: 'ri-line-chart-line',
      title: '股權投資媒合',
      description: '協助企業尋找策略投資人或財務投資人，促成股權投資合作',
      features: [
        '投資人資源媒合',
        '企業估值諮詢',
        '投資條件協商',
        '法律文件協助'
      ],
      color: 'green'
    },
    {
      icon: 'ri-exchange-line',
      title: '應收帳款融資',
      description: '將應收帳款轉換為即時現金流，解決企業短期資金週轉需求',
      features: [
        '應收帳款評估',
        '快速審核流程',
        '彈性融資額度',
        '專業風險管理'
      ],
      color: 'purple'
    },
    {
      icon: 'ri-shield-check-line',
      title: '信用保證服務',
      description: '協助企業取得信用保證，提高融資成功率與降低融資成本',
      features: [
        '信保基金申請',
        '保證條件評估',
        '申請文件準備',
        '後續追蹤服務'
      ],
      color: 'red'
    },
    {
      icon: 'ri-customer-service-line',
      title: '財務顧問諮詢',
      description: '提供專業財務規劃建議，協助企業優化資金結構與財務管理',
      features: [
        '財務健檢分析',
        '資金規劃建議',
        '融資策略擬定',
        '長期顧問服務'
      ],
      color: 'teal'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: '需求諮詢',
      description: '了解您的資金需求與企業狀況'
    },
    {
      step: '02',
      title: '方案評估',
      description: '分析最適合的融資方案與管道'
    },
    {
      step: '03',
      title: '資源媒合',
      description: '媒合最適合的資金提供方'
    },
    {
      step: '04',
      title: '協商簽約',
      description: '協助條件協商與合約簽訂'
    },
    {
      step: '05',
      title: '撥款完成',
      description: '確保資金順利到位'
    }
  ];

  const documentRequirements = [
    {
      category: '青年創業專案',
      basicDocs: [
        '負責人身分證影本（正反面）',
        '公司設立或商業登記核准函',
        '公司章程／合夥契約書'
      ],
      financialDocs: [
        '創業計畫書（需含3年財務預測）',
        '創業輔導課程證明（20小時以上）',
        '負責人個人聯徵報告'
      ],
      otherDocs: [
        '青年創業貸款切結書',
        '聯徵查詢同意書',
        '借款申請書'
      ]
    },
    {
      category: '中小企業信保專案',
      basicDocs: [
        '公司變更登記事項卡',
        '負責人及配偶身分證影本',
        '公司章程／股東名冊'
      ],
      financialDocs: [
        '近三年資產負債表／損益表（需用印）',
        '近一年 401/403 報表（含稅額申報書）',
        '存摺近半年往來明細'
      ],
      otherDocs: [
        '營運計畫書',
        '主要合約／訂單／發票影本',
        '聯徵查詢同意書'
      ]
    },
    {
      category: '微型企業／小頭家專案',
      basicDocs: [
        '負責人身分證影本（正反面）',
        '稅籍登記證明／商業登記抄本'
      ],
      financialDocs: [
        '近半年 401/403 報表（若有）',
        '主要存摺近一年往來明細',
        '相關證照或執業證明'
      ],
      otherDocs: [
        '簡易貸款申請書',
        '聯徵查詢同意書',
        '切結書'
      ]
    },
    {
      category: '文化創意產業優惠專案',
      basicDocs: [
        '負責人身分證影本',
        '公司或商業登記證明'
      ],
      financialDocs: [
        '文創產業證明文件（獎項／專利／著作權）',
        '近三年財報或稅務報表',
        '401/403 報表'
      ],
      otherDocs: [
        '文創事業計畫書',
        '無形資產鑑價報告（視個案）',
        '推薦函或合作意向書'
      ]
    },
    {
      category: '低碳智慧化設備補助',
      basicDocs: [
        '工廠登記證／公司登記證明',
        '最近一期 401 表'
      ],
      financialDocs: [
        '設備購置合約或正式報價單',
        '自籌款財力證明（存摺／定存）',
        '近三年財報'
      ],
      otherDocs: [
        '低碳智慧化轉型計畫書',
        '設備型錄與規格說明',
        '預期效益評估報告'
      ]
    },
    {
      category: '再生能源／節能設備專案',
      basicDocs: [
        '公司登記／工廠登記證明',
        '能源局再生能源設備備案定函'
      ],
      financialDocs: [
        '合電購售電合約（PPA）',
        '設備工程合約／報價單',
        '近三年財報'
      ],
      otherDocs: [
        '再生能源設置計畫書',
        '施工許可證／環評證明',
        '土地／建物使用同意書'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              專業服務方案
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              朋睿資融提供全方位的企業融資媒合服務，從傳統銀行貸款到股權投資，我們都能為您找到最適合的資金解決方案
            </p>
          </div>
        </div>
      </section>

      {/* Funding Plans Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              服務方案／資金專案選擇
            </h2>
            <p className="text-lg text-gray-600">
              根據您的企業類型與需求，選擇最適合的資金專案
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {fundingPlans.map((plan, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-amber-500 hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {plan.title}
                    </h3>
                    <p className="text-sm text-gray-500 font-medium">
                      {plan.target}
                    </p>
                  </div>
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 flex-shrink-0 ml-4">
                    <i className={`${plan.icon} text-2xl`}></i>
                  </div>
                </div>

                <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                  {plan.description}
                </p>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5 mr-2 flex-shrink-0"></i>
                      <span className="text-sm text-gray-700">{highlight}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => {
                    setSelectedPlan(index);
                    navigate('/contact');
                  }}
                  className={`w-full py-3 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer ${
                    selectedPlan === index
                      ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-amber-500 hover:to-orange-600 hover:text-white'
                  }`}
                >
                  預約諮詢此方案
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              完整服務項目
            </h2>
            <p className="text-lg text-gray-600">
              提供多元化的融資媒合與財務顧問服務
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 border border-gray-200 hover:border-amber-500 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 flex items-center justify-center rounded-xl bg-${service.color}-100 text-${service.color}-600 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${service.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-amber-500 text-lg mt-0.5 mr-2 flex-shrink-0"></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Document Requirements Section - NEW */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <i className="ri-file-list-3-line text-4xl text-amber-600 mr-3"></i>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                各類專案申請必備文件清單
              </h2>
            </div>
            <p className="text-base text-gray-600 max-w-4xl mx-auto leading-relaxed">
              事前準備越齊全，核貸速度越快。以下為各類方案之基礎必備文件，實際需求可能因個案狀況與銀行要求而異。
            </p>
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <th className="px-6 py-4 text-left text-base font-bold text-gray-900 border-b-2 border-r border-gray-200 w-1/5">
                      專案類型
                    </th>
                    <th className="px-6 py-4 text-left text-base font-bold text-gray-900 border-b-2 border-r border-gray-200 w-1/4">
                      基本資格文件
                    </th>
                    <th className="px-6 py-4 text-left text-base font-bold text-gray-900 border-b-2 border-r border-gray-200 w-1/4">
                      財務／營收證明
                    </th>
                    <th className="px-6 py-4 text-left text-base font-bold text-gray-900 border-b-2 w-1/4">
                      其他／計畫書
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {documentRequirements.map((item, index) => (
                    <tr key={index} className="hover:bg-amber-50/30 transition-colors">
                      <td className="px-6 py-5 border-b border-r border-gray-200 align-top">
                        <div className="font-bold text-gray-900 text-sm">
                          {item.category}
                        </div>
                      </td>
                      <td className="px-6 py-5 border-b border-r border-gray-200 align-top">
                        <ul className="space-y-2">
                          {item.basicDocs.map((doc, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-700">
                              <i className="ri-file-text-line text-amber-600 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                              <span>{doc}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-5 border-b border-r border-gray-200 align-top">
                        <ul className="space-y-2">
                          {item.financialDocs.map((doc, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-700">
                              <i className="ri-file-chart-line text-amber-600 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                              <span>{doc}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-5 border-b border-gray-200 align-top">
                        <ul className="space-y-2">
                          {item.otherDocs.map((doc, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-700">
                              <i className="ri-file-paper-line text-amber-600 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                              <span>{doc}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-6">
            {documentRequirements.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4">
                  <h3 className="text-lg font-bold text-white">{item.category}</h3>
                </div>
                
                <div className="p-6 space-y-6">
                  <div>
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-file-list-line text-amber-600 mr-2"></i>
                      基本資格文件
                    </h4>
                    <ul className="space-y-2">
                      {item.basicDocs.map((doc, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-amber-600 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-bar-chart-line text-amber-600 mr-2"></i>
                      財務／營收證明
                    </h4>
                    <ul className="space-y-2">
                      {item.financialDocs.map((doc, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-amber-600 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-file-paper-2-line text-amber-600 mr-2"></i>
                      其他／計畫書
                    </h4>
                    <ul className="space-y-2">
                      {item.otherDocs.map((doc, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-amber-600 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Warning Notice */}
          <div className="mt-12 bg-amber-50 border-2 border-amber-200 rounded-xl p-6">
            <div className="flex items-start">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3">
                <i className="ri-error-warning-fill text-2xl text-amber-600"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  注意事項：
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  上述文件僅供參考，若您缺少部分文件（如無 401 報表或無計畫書），請勿擔心，朋睿顧問將協助您進行替代方案規劃或輔導撰寫。
                </p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-block px-10 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-semibold text-lg hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              預約免費諮詢
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              服務流程
            </h2>
            <p className="text-lg text-gray-600">
              簡單五步驟，快速解決您的資金需求
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 transform -translate-y-1/2"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 relative">
              {processSteps.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-amber-500 transition-all duration-300 hover:shadow-lg">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white text-2xl font-bold mb-4 mx-auto">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 text-center leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                為什麼選擇朋睿資融？
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 mr-4 flex-shrink-0">
                    <i className="ri-team-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      專業團隊
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      擁有豐富的金融產業經驗，深入了解各種融資管道與產品特性
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 mr-4 flex-shrink-0">
                    <i className="ri-links-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      廣泛資源
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      整合銀行、民間金主、創投等多元資金來源，提供最佳選擇
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 mr-4 flex-shrink-0">
                    <i className="ri-flashlight-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      快速高效
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      簡化申請流程，大幅縮短媒合時間，讓資金快速到位
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 mr-4 flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      安全保密
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      嚴格保護客戶資料，確保交易過程安全可靠
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-[500px]">
              <img
                src="https://public.readdy.ai/ai/img_res/93a368e58f3176b39c159d0f406f3c6c.jpg"
                alt="專業金融服務團隊"
                className="w-full h-full object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            不確定適合哪種方案？
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            每家企業的狀況都是獨一無二的。讓我們的專業顧問為您進行免費的財務健檢與需求盤點，規劃最適合您的資金路徑。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="px-8 py-4 bg-white text-amber-600 text-lg font-semibold rounded-lg hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              免費諮詢
            </Link>
            <Link
              to="/cases"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="px-8 py-4 bg-transparent text-white text-lg font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
            >
              查看成功案例
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* LINE 客服按鈕 */}
      <a
        href="https://line.me/R/ti/p/@678gwbht"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-28 right-6 z-40 w-14 h-14 bg-[#06C755] rounded-full flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform duration-300 group"
        aria-label="LINE 即時諮詢"
      >
        <i className="ri-line-fill text-2xl"></i>
        <span className="absolute right-16 bg-gray-900 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          LINE 即時諮詢
        </span>
      </a>
    </div>
  );
}
